import { Crown, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getSettingsPath } from '../../utils/paths';

interface LockedFeatureProps {
  feature: string;
  requiredTier?: 'starter' | 'pro';
  children: React.ReactNode;
}

const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 1,
  'pro': 2,
  'enterprise': 3
};

const tierInfo = {
  starter: {
    name: 'Starter',
    price: '$49/mo',
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-500',
  },
  pro: {
    name: 'Pro',
    price: '$99/mo',
    color: 'purple',
    gradient: 'from-purple-500 to-pink-500',
  },
};

export function LockedFeature({ feature, requiredTier = 'starter', children }: LockedFeatureProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const userLevel = tierLevel[user?.subscription_tier || 'free'] || 0;
  const requiredLevel = tierLevel[requiredTier] || 1;

  // User has access - render children normally
  if (userLevel >= requiredLevel) {
    return <>{children}</>;
  }

  const tier = tierInfo[requiredTier];

  return (
    <div className="relative min-h-[400px]">
      {/* Greyed out content preview */}
      <div className="opacity-30 pointer-events-none select-none grayscale">
        {children}
      </div>

      {/* Upgrade overlay - positioned at top */}
      <div className="absolute inset-x-0 top-0 flex justify-center pt-8 z-10">
        <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-2xl shadow-2xl p-8 max-w-md mx-4">
          <div className="text-center">
            {/* Lock icon */}
            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${tier.gradient} flex items-center justify-center`}>
              <Lock className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">
              {tier.name} Feature
            </h3>

            {/* Description */}
            <p className="text-[var(--color-text-muted)] mb-4">
              {feature}
            </p>

            {/* Price badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-bg-secondary)] mb-6">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                Starting at {tier.price}
              </span>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => navigate(getSettingsPath('subscription'))}
              className={`w-full px-6 py-3 bg-gradient-to-r ${tier.gradient} text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl cursor-pointer`}
            >
              Upgrade to {tier.name}
            </button>

            {/* Features hint */}
            <p className="text-xs text-[var(--color-text-muted)] mt-4">
              Unlock this feature and more with {tier.name}
            </p>
          </div>
        </div>
      </div>

      {/* Gradient fade at bottom of content */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[var(--color-bg-primary)] to-transparent pointer-events-none" />
    </div>
  );
}
